import React, { useEffect, useState } from "react";
import { memberFetch } from "../../lib/memberApi";
import { toast } from "sonner";
import { CreditCard, FileText, Loader2 } from "lucide-react";
import { fmtDate } from "./_shared";

export default function BillingTab() {
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(null);

  const load = async () => {
    try {
      const d = await memberFetch("/member/billing");
      setData(d);
    } catch (e) { toast.error(e.message || "Failed to load billing"); }
  };
  useEffect(() => { load(); }, []);

  const pay = async (invoiceId) => {
    setPaying(invoiceId);
    try {
      const r = await memberFetch(`/member/billing/invoices/${invoiceId}/pay`, {
        method: "POST",
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      window.location.href = r.url;
    } catch (e) {
      toast.error(e.message || "Could not start payment");
      setPaying(null);
    }
  };

  if (!data) return <div className="text-zinc-500">Loading billing...</div>;

  const fmtMoney = (n) => `$${Number(n).toFixed(2)}`;
  const fmtPeriod = (p) => {
    if (!p) return "—";
    const [y, m] = p.split("-");
    return new Date(Number(y), Number(m) - 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  };
  const statusBadge = (s) => {
    const cls =
      s === "paid" ? "border-zinc-700 text-zinc-400" :
      s === "overdue" ? "bg-[#E5B43A] text-black border-[#E5B43A]" :
      "border-[#E5B43A] text-[#E5B43A]";
    return <span className={`inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase border ${cls}`}>{s}</span>;
  };

  const activePlans = data.plans.filter((p) => p.active);
  const inactivePlans = data.plans.filter((p) => !p.active);

  return (
    <div data-testid="tab-billing">
      <h2 className="font-display text-3xl mb-2">Billing</h2>
      <p className="text-zinc-500 text-sm mb-6">Your training service invoices generate automatically each month. Pay with card via Stripe.</p>

      {/* Balance hero */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="md:col-span-1 border border-[#E5B43A] bg-[#141414] p-6" data-testid="balance-card">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-2">Balance Due</div>
          <div className="font-display text-5xl md:text-6xl">{fmtMoney(data.balance_due)}</div>
          <div className="text-xs text-zinc-500 mt-2">{data.invoices.filter((i) => i.status !== "paid").length} open invoice(s)</div>
        </div>
        <div className="md:col-span-2 border border-zinc-800 bg-[#141414] p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-3">Active Service Plans</div>
          {activePlans.length === 0 ? (
            <p className="text-zinc-500 text-sm italic">No active service plans. Davon assigns plans from admin.</p>
          ) : (
            <ul className="space-y-2">
              {activePlans.map((p) => (
                <li key={p.id} className="flex items-center justify-between py-2 border-b border-zinc-900 last:border-0" data-testid={`active-plan-${p.id}`}>
                  <div>
                    <div className="font-display text-lg">{p.plan_name}</div>
                    <div className="text-xs text-zinc-500">Billed on day {p.billing_day} · started {new Date(p.started_at).toLocaleDateString()}</div>
                  </div>
                  <div className="font-display text-xl">{fmtMoney(p.amount)}<span className="text-xs text-zinc-500"> / mo</span></div>
                </li>
              ))}
            </ul>
          )}
          {inactivePlans.length > 0 && (
            <div className="mt-4 pt-3 border-t border-zinc-900 text-xs text-zinc-600 break-words">
              Inactive: {inactivePlans.map((p) => p.plan_name).join(", ")}
            </div>
          )}
        </div>
      </div>

      {/* Invoices table */}
      <h3 className="font-display text-2xl mb-4">Invoices ({data.invoices.length})</h3>
      {data.invoices.length === 0 ? (
        <p className="text-zinc-500 text-sm italic">No invoices yet.</p>
      ) : (
        <div className="border border-zinc-800 bg-[#141414] overflow-hidden">
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#0A0A0A] z-10">
                <tr className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 border-b border-zinc-800">
                  <th className="text-left px-5 py-3">Plan</th>
                  <th className="text-left px-5 py-3 hidden md:table-cell">Period</th>
                  <th className="text-left px-5 py-3">Amount</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3 hidden md:table-cell">Due</th>
                  <th className="text-right px-5 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-zinc-900 last:border-0 hover:bg-[#1a1a1a]" data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-5 py-4 font-semibold whitespace-nowrap">{inv.plan_name}</td>
                    <td className="px-5 py-4 text-zinc-400 hidden md:table-cell whitespace-nowrap">{fmtPeriod(inv.billing_period)}</td>
                    <td className="px-5 py-4">{fmtMoney(inv.amount)}</td>
                    <td className="px-5 py-4">{statusBadge(inv.status)}</td>
                    <td className="px-5 py-4 text-zinc-500 text-xs hidden md:table-cell whitespace-nowrap">{new Date(inv.due_date).toLocaleDateString()}</td>
                    <td className="px-5 py-4 text-right">
                      {inv.status === "paid" ? (
                        <span className="text-xs text-zinc-500">Paid {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : ""}</span>
                      ) : (
                        <button
                          onClick={() => pay(inv.id)}
                          disabled={paying === inv.id}
                          data-testid={`pay-invoice-${inv.id}`}
                          className="inline-flex items-center gap-2 bg-[#E5B43A] text-black px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors disabled:opacity-60"
                        >
                          {paying === inv.id ? <Loader2 className="animate-spin" size={12} /> : <CreditCard size={12} />}
                          Pay {fmtMoney(inv.amount)}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
